import { useEffect, useState } from "react";
import { Plus, Trash2, CheckCircle2, Circle, Target, Flame } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";

const CATEGORIES = ["Trading", "Career", "Business", "Health", "Faith", "Personal"];

export default function Tasks() {
    const [tab, setTab] = useState("tasks");
    return (
        <div className="space-y-8" data-testid="tasks-page">
            <PageHeader eyebrow="Life beyond the chart" title="Personal Life" subtitle="Tasks, goals and habits across every area of life — not just trading." />

            <div className="flex gap-2 border-b border-white/5">
                {[
                    { id: "tasks", label: "Tasks" },
                    { id: "goals", label: "Goals" },
                    { id: "habits", label: "Habits" },
                ].map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`life-tab-${t.id}`}
                        className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === t.id ? "border-[#D4AF37] text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "tasks" && <TasksPanel />}
            {tab === "goals" && <GoalsPanel />}
            {tab === "habits" && <HabitsPanel />}
        </div>
    );
}

function TasksPanel() {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("All");
    const [form, setForm] = useState({ title: "", category: "Personal", due_date: "", description: "" });

    const load = async () => {
        const { data } = await api.get("/tasks");
        setList(data.sort((a, b) => (a.completed === b.completed ? (a.due_date || "").localeCompare(b.due_date || "") : a.completed ? 1 : -1)));
    };
    useEffect(() => { load(); }, []);

    const add = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        await api.post("/tasks", form);
        setForm({ title: "", category: "Personal", due_date: "", description: "" });
        load();
    };
    const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { completed: !t.completed }); load(); };
    const del = async (id) => { await api.delete(`/tasks/${id}`); load(); };

    const shown = filter === "All" ? list : list.filter((t) => t.category === filter);

    return (
        <div className="space-y-5" data-testid="tasks-panel">
            <form onSubmit={add} className="twp-card p-5 grid md:grid-cols-4 gap-2">
                <input placeholder="Task title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="task-title" />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-2">
                    <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E]" data-testid="task-add"><Plus className="w-4 h-4" /></button>
                </div>
            </form>

            <div className="flex gap-2 flex-wrap">
                {["All", ...CATEGORIES].map((c) => (
                    <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-md text-xs ${filter === c ? "bg-[#D4AF37] text-[#0E0E10]" : "text-zinc-400 border border-white/10 hover:border-white/20"}`} data-testid={`filter-cat-${c}`}>{c}</button>
                ))}
            </div>

            <ul className="divide-y divide-white/5 twp-card">
                {shown.map((t) => (
                    <li key={t.id} className="flex items-start gap-3 px-4 py-3" data-testid={`task-${t.id}`}>
                        <button onClick={() => toggle(t)} className="mt-1" data-testid={`task-toggle-${t.id}`}>
                            {t.completed ? <CheckCircle2 className="w-4 h-4 text-[#5B8A62]" strokeWidth={1.5} /> : <Circle className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />}
                        </button>
                        <div className="flex-1 min-w-0">
                            <div className={`text-sm ${t.completed ? "line-through text-zinc-500" : ""}`}>{t.title}</div>
                            <div className="caption mt-0.5">{t.category}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
                        </div>
                        <button onClick={() => del(t.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </li>
                ))}
                {shown.length === 0 && <li className="px-4 py-6 text-sm text-zinc-500">No tasks.</li>}
            </ul>
        </div>
    );
}

function GoalsPanel() {
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ title: "", category: "Personal", description: "", target_date: "", progress: 0 });
    const load = async () => { const { data } = await api.get("/goals"); setList(data); };
    useEffect(() => { load(); }, []);
    const add = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        await api.post("/goals", { ...form, progress: parseInt(form.progress) || 0 });
        setForm({ title: "", category: "Personal", description: "", target_date: "", progress: 0 });
        load();
    };
    const update = async (g, patch) => { await api.put(`/goals/${g.id}`, patch); load(); };
    const del = async (id) => { await api.delete(`/goals/${id}`); load(); };

    return (
        <div className="space-y-5" data-testid="goals-panel">
            <form onSubmit={add} className="twp-card p-5 grid md:grid-cols-4 gap-2">
                <input placeholder="Goal title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="goal-title" />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-2">
                    <input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E]" data-testid="goal-add"><Plus className="w-4 h-4" /></button>
                </div>
                <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="md:col-span-4 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            </form>

            <div className="grid md:grid-cols-2 gap-4">
                {list.map((g) => (
                    <div key={g.id} className="twp-card p-5" data-testid={`goal-${g.id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="caption">{g.category}{g.target_date ? ` · ${g.target_date}` : ""}</div>
                                <h3 className="font-serif text-lg mt-1">{g.title}</h3>
                                {g.description && <p className="text-sm text-zinc-400 mt-2">{g.description}</p>}
                            </div>
                            <button onClick={() => del(g.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="mt-4">
                            <div className="flex justify-between text-xs text-zinc-500 mb-1"><span>Progress</span><span className="gold font-mono">{g.progress}%</span></div>
                            <input type="range" min={0} max={100} value={g.progress} onChange={(e) => update(g, { progress: parseInt(e.target.value) })} className="w-full" data-testid={`goal-progress-${g.id}`} />
                        </div>
                    </div>
                ))}
                {list.length === 0 && <div className="text-sm text-zinc-500">No goals yet.</div>}
            </div>
        </div>
    );
}

function HabitsPanel() {
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ name: "", category: "Personal", frequency: "daily" });
    const load = async () => { const { data } = await api.get("/habits"); setList(data); };
    useEffect(() => { load(); }, []);
    const add = async (e) => {
        e.preventDefault();
        if (!form.name) return;
        await api.post("/habits", { ...form, history: [] });
        setForm({ name: "", category: "Personal", frequency: "daily" });
        load();
    };
    const markToday = async (h) => {
        const today = new Date().toISOString().slice(0, 10);
        const history = Array.from(new Set([...(h.history || []), today]));
        await api.put(`/habits/${h.id}`, { history });
        load();
    };
    const del = async (id) => { await api.delete(`/habits/${id}`); load(); };

    const streak = (history) => {
        if (!history || history.length === 0) return 0;
        const set = new Set(history);
        let d = new Date();
        let s = 0;
        while (set.has(d.toISOString().slice(0, 10))) {
            s++;
            d.setDate(d.getDate() - 1);
        }
        return s;
    };

    return (
        <div className="space-y-5" data-testid="habits-panel">
            <form onSubmit={add} className="twp-card p-5 grid md:grid-cols-4 gap-2">
                <input placeholder="Habit name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="habit-name" />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-2">
                    <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm">
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                    </select>
                    <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E]" data-testid="habit-add"><Plus className="w-4 h-4" /></button>
                </div>
            </form>
            <div className="grid md:grid-cols-2 gap-4">
                {list.map((h) => (
                    <div key={h.id} className="twp-card p-5" data-testid={`habit-${h.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="caption">{h.category} · {h.frequency}</div>
                                <h3 className="font-serif text-lg mt-1">{h.name}</h3>
                            </div>
                            <button onClick={() => del(h.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="mt-4 flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Flame className="w-4 h-4 gold" strokeWidth={1.5} />
                                <span className="font-mono gold">{streak(h.history)}</span>
                                <span className="caption">day streak</span>
                            </div>
                            <button onClick={() => markToday(h)} className="ml-auto px-3 py-1.5 rounded-md border border-white/10 hover:border-[#D4AF37]/40 text-xs" data-testid={`habit-mark-${h.id}`}>Mark today</button>
                        </div>
                    </div>
                ))}
                {list.length === 0 && <div className="text-sm text-zinc-500">No habits yet.</div>}
            </div>
        </div>
    );
}
